/**
 * canyon-rocks.ts — Stone-canyon framing for dry "Fish" zones (RENDER side).
 *
 * The map has depressed areas ("Fish, ..." regions in regions.json) where water
 * was originally intended but isn't rendered. This module frames those hollows
 * as stone canyons: cliff/canyon-wall meshes along each region's perimeter, plus
 * a few large-rock clusters inside.
 *
 * The PLACEMENT (positions / scales / rotations) now lives in the shared
 * deterministic generator `generateCanyonPlacements` (@tribez/shared), so the
 * server gives these same rocks/walls collision + navmesh footprints — no more
 * walking through canyon boulders. This module is purely the render pass: turn
 * each placement into an InstancedMesh matrix (sampling terrain height for Y),
 * using the same toon-instancing pattern as doodads.ts.
 */

import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { generateCanyonPlacements, type CanyonRegion } from "@tribez/shared";
import type { AssetBundle } from "../assets";
import { toToon, bakeBaseAO } from "../toon";
import { emitChunkedInstances } from "./doodads";
import type { RegionRow } from "./types";

type Renderable = { geom: THREE.BufferGeometry; mat: THREE.Material };

// `cutout` is decided per mesh key by the caller from its placed world height:
// tall canyon walls / cliffs keep the see-through fade (the hero can stand
// behind them), short cluster rocks opt out so they don't stipple (playtest
// 2026-06-25). See toon.ts CUTOUT_MIN_HEIGHT.
function extractRenderables(gltf: GLTF, cutout: boolean): Renderable[] {
  const out: Renderable[] = [];
  gltf.scene.traverse((node) => {
    const m = node as THREE.Mesh;
    if (!m.isMesh) return;
    const mat = Array.isArray(m.material) ? m.material[0]! : m.material;
    bakeBaseAO(m.geometry);
    out.push({ geom: m.geometry, mat: toToon(mat, { noiseScale: 2.5, cutout }) });
  });
  return out;
}

// Which canyon meshes keep the see-through cutout: the tall ones the hero can
// stand behind (canyon walls, cliffs, large rocks). Short cluster rocks (medium/
// small) opt out so they don't stipple away (playtest 2026-06-25 occlusion gate).
function canyonKeepsCutout(meshKey: string): boolean {
  return meshKey.startsWith("exCanyonW")
    || meshKey.startsWith("exCliff")
    || meshKey.startsWith("exRockL");
}

// Cutout-eligible mesh keys (canyonKeepsCutout) get their own per-key fadeAttrs
// map, matching doodads.ts's "same doodad's primitives fade in lockstep"
// pattern — reused here via the shared emitChunkedInstances so canyon rocks
// wire into the whole-canopy fade + get chunked frustum culling the same as
// forest doodads, instead of one giant always-in-frustum InstancedMesh per
// mesh key (bug fix 2026-07-02: canyon rocks previously had NO occlusion
// coverage at all once the screen-space hero-porthole circle — the only thing
// that ever gave them any see-through relief — was deleted; see world.ts for
// why attaching to the doodad "objects" root is what plugs both the
// canopy-fade scan AND the x-ray occluder list back in for free).
function buildInstances(
  root: THREE.Object3D,
  renderables: Renderable[],
  matrices: THREE.Matrix4[],
  name: string,
  cutout: boolean,
): void {
  if (matrices.length === 0) return;
  const fadeAttrs = cutout ? new Map<string, THREE.InstancedBufferAttribute>() : undefined;
  for (let ri = 0; ri < renderables.length; ri++) {
    const r = renderables[ri]!;
    emitChunkedInstances(root, r.geom, r.mat, matrices, `${name}:${ri}`, true, fadeAttrs);
  }
}

/**
 * Render the canyon framing for the dry "Fish" basins. Placement comes from the
 * shared generator (so it matches server collision/navmesh); we just sample
 * terrain height per placement and instance the meshes.
 *
 * `objectsRoot` is the SAME group `buildObjects()` (doodads.ts) returns for the
 * forest — canyon rocks are added as its children (not a separate scene-level
 * group) so every consumer that already walks/raycasts the doodad "objects"
 * root (whole-canopy fade's scan, the x-ray silhouette occluder list) picks
 * them up automatically, with zero extra wiring at the call sites.
 */
export function buildCanyonRocks(
  objectsRoot: THREE.Object3D,
  assets: AssetBundle,
  sampleHeight: (x: number, z: number) => number,
  regions: RegionRow[],
): void {
  const placements = generateCanyonPlacements(regions as unknown as CanyonRegion[]);
  if (placements.length === 0) return;

  // Pre-extract renderables once per mesh key referenced by the placements,
  // gating the cutout on the mesh type (tall walls/cliffs/large-rocks fade).
  const renderableMap = new Map<string, Renderable[]>();
  for (const p of placements) {
    if (renderableMap.has(p.meshKey)) continue;
    const gltf = assets.nature[p.meshKey as keyof AssetBundle["nature"]];
    if (!gltf) { renderableMap.set(p.meshKey, []); continue; }
    renderableMap.set(p.meshKey, extractRenderables(gltf, canyonKeepsCutout(p.meshKey)));
  }

  // Accumulate matrices per mesh key, then flush to chunked InstancedMeshes
  // (emitChunkedInstances splits by grid cell for per-cell frustum culling).
  const matrixBuckets = new Map<string, THREE.Matrix4[]>();
  const dummy = new THREE.Object3D();
  for (const p of placements) {
    const rr = renderableMap.get(p.meshKey);
    if (!rr || rr.length === 0) continue;
    dummy.position.set(p.x, sampleHeight(p.x, p.z), p.z);
    dummy.rotation.set(0, p.rotY, 0);
    dummy.scale.setScalar(p.scale);
    dummy.updateMatrix();
    let arr = matrixBuckets.get(p.meshKey);
    if (!arr) { arr = []; matrixBuckets.set(p.meshKey, arr); }
    arr.push(dummy.matrix.clone());
  }

  for (const [key, matrices] of matrixBuckets) {
    const renderables = renderableMap.get(key);
    if (renderables) buildInstances(objectsRoot, renderables, matrices, `canyon-rocks:${key}`, canyonKeepsCutout(key));
  }
}
